import { ATMO, FLIGHT } from '../config/tuning';
import type { Airframe } from '../config/airframes';

/**
 * Aerodynamic coefficients and the algebra around them (SPEC §3.2.2).
 *
 * Every function here is pure and side-effect free, so the parts of the flight model that must
 * be provably correct can be tested without running a simulation. The forces themselves are
 * assembled in FlightModel.
 *
 * Units: radians for angles, m/s for speeds, kg/m³ for density, Pa for pressure, N for force.
 */

/** Smoothstep on [0,1]. */
function smoothstep(t: number): number {
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  return clamped * clamped * (3 - 2 * clamped);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/**
 * Lift coefficient against angle of attack, with a *gradual* post-stall decay.
 *
 * Below the stall it is the usual straight line, CL0 + CLα·α. Above it, CL eases down to
 * `postStallClFactor·CLmax` across a 12° band and then on toward a deep-stall residual by 45°.
 * The gradualness is the whole point: a discontinuity at the stall makes the aircraft feel
 * broken rather than mishandled, and the player learns nothing from it.
 *
 * The curve is mirrored for negative α. A cambered wing is not truly symmetric, but with
 * CL0 = 0.08 the asymmetry is far below what anyone can feel, and symmetry keeps inverted
 * flight honest.
 */
export function liftCoefficient(alphaRad: number, airframe: Airframe): number {
  const sign = alphaRad < 0 ? -1 : 1;
  const alpha = Math.abs(alphaRad);
  const stall = airframe.alphaStallRad;

  if (alpha <= stall) {
    return sign * (airframe.cl0 + airframe.clAlpha * alpha);
  }

  const postStallEnd = stall + airframe.postStallRangeRad;
  const clPostStall = FLIGHT.postStallClFactor * airframe.clMax;

  if (alpha <= postStallEnd) {
    const t = (alpha - stall) / airframe.postStallRangeRad;
    return sign * lerp(airframe.clAtStall, clPostStall, smoothstep(t));
  }

  const clDeepStall = FLIGHT.deepStallClFactor * airframe.clMax;
  const deepEnd = airframe.deepStallAlphaRad;
  if (alpha >= deepEnd) return sign * clDeepStall;

  const t = (alpha - postStallEnd) / (deepEnd - postStallEnd);
  return sign * lerp(clPostStall, clDeepStall, smoothstep(t));
}

/**
 * Drag coefficient: parasitic, induced, and separated-flow terms.
 *
 * The induced term k·CL² is what makes hard turns bleed speed, and it carries the entire
 * "energy is currency" pillar on its own. The separated term only matters past the stall, where
 * it is what actually decelerates a departed aircraft.
 */
export function dragCoefficient(
  liftCoefficientValue: number,
  alphaRad: number,
  airframe: Airframe,
): number {
  const induced = airframe.inducedDragK * liftCoefficientValue * liftCoefficientValue;
  const separationSin = Math.sin(clamp(alphaRad, -FLIGHT.maxAlphaRad, FLIGHT.maxAlphaRad));
  const separated =
    FLIGHT.separatedDragCd * separationSin * separationSin * separationFraction(alphaRad, airframe);
  return airframe.cd0 + induced + separated;
}

/**
 * Fraction of the wing in separated flow, 0 to 1.
 *
 * Gated to the approach to the stall rather than applied from zero α. Attached flow does not
 * produce bluff-body drag, and letting sin²α run from the origin adds more drag at 7° of α than
 * the entire parasitic term — which quietly makes slow flight unaffordable and reads as an
 * underpowered engine rather than as separation.
 */
export function separationFraction(alphaRad: number, airframe: Airframe): number {
  const onset = FLIGHT.separationOnsetFraction * airframe.alphaStallRad;
  const full = FLIGHT.separationFullFraction * airframe.alphaStallRad;
  return smoothstep((Math.abs(alphaRad) - onset) / (full - onset));
}

/** Side-force coefficient. Cy_β is negative, so the force opposes sideslip and weathervanes. */
export function sideForceCoefficient(betaRad: number, airframe: Airframe): number {
  return airframe.cyBeta * betaRad;
}

/** Dynamic pressure q = ½·ρ·V², pascals. */
export function dynamicPressurePa(densityKgM3: number, airspeedMs: number): number {
  return 0.5 * densityKgM3 * airspeedMs * airspeedMs;
}

/**
 * How far past the stall the wing is, 0 (unstalled) to 1 (fully developed, at the end of the
 * post-stall band). Drives autorotation, control loss, and the HUD stall warning.
 */
export function stallDepth(alphaRad: number, airframe: Airframe): number {
  const excess = Math.abs(alphaRad) - airframe.alphaStallRad;
  if (excess <= 0) return 0;
  return clamp(excess / airframe.postStallRangeRad, 0, 1);
}

/**
 * Stall speed at a given load factor, m/s.
 *
 * V_s(n) = √(2·n·m·g / (ρ·S·CL_max)) = V_s(1)·√n. The √n relationship is the core skill ceiling
 * of the flight model: at 6 g the stall speed more than doubles, so a hard break at low speed
 * departs the aircraft (SPEC §3.2.4).
 */
export function stallSpeedMs(
  airframe: Airframe,
  densityKgM3: number,
  loadFactor: number,
): number {
  const n = Math.abs(loadFactor);
  return Math.sqrt(
    (2 * n * airframe.massKg * ATMO.G) / (densityKgM3 * airframe.wingAreaM2 * airframe.clAtStall),
  );
}

/**
 * Control authority from dynamic pressure, normalised at cruise and clamped (SPEC §3.2.3).
 *
 * This is why slow flight feels mushy and fast flight feels sharp. It is implemented rather
 * than faked because it is the mechanism that teaches the player to keep their speed up.
 */
export function controlAuthority(dynamicPressureValuePa: number, airframe: Airframe): number {
  return clamp(
    dynamicPressureValuePa / airframe.dynamicPressureCruisePa,
    FLIGHT.authorityMin,
    FLIGHT.authorityMax,
  );
}

/**
 * Angle of attack from body-frame velocity components, radians.
 * `forwardMs` is along the nose, `upMs` along the body-up axis. Positive α means the relative
 * wind arrives from below the nose.
 */
export function angleOfAttackRad(forwardMs: number, upMs: number): number {
  return Math.atan2(-upMs, forwardMs);
}

/**
 * Sideslip from body-frame velocity, radians. Positive β means the aircraft is moving to its
 * own right — relative wind from the right — which is what the fin weathervanes against.
 */
export function sideslipRad(rightMs: number, airspeedMs: number): number {
  if (airspeedMs < FLIGHT.minAeroAirspeedMs) return 0;
  return Math.asin(clamp(rightMs / airspeedMs, -1, 1));
}

export interface TrimSolution {
  /** Angle of attack that supports the load, radians. */
  alphaRad: number;
  /** Throttle setting that balances drag, 0..1. */
  throttle: number;
}

/**
 * Level-flight trim: the α that supports the weight at this speed and the throttle that balances
 * the resulting drag.
 *
 * Used to spawn the aircraft already flying rather than sinking — with a rate-command rotation
 * model there is no pitch stiffness to find trim on its own, so an untrimmed spawn settles into
 * a descent and the "flies stably hands-off" criterion becomes untestable.
 */
export function computeTrim(
  airframe: Airframe,
  densityKgM3: number,
  airspeedMs: number,
  loadFactor = 1,
): TrimSolution {
  const q = dynamicPressurePa(densityKgM3, airspeedMs);
  const qS = q * airframe.wingAreaM2;
  const weightN = loadFactor * airframe.massKg * ATMO.G;

  // Two coupled equilibria, solved by fixed-point iteration (it converges in three passes):
  //
  //   perpendicular to the flight path:  L·cos α + T·sin α = W
  //   along the flight path:             T·cos α = D + L·sin α
  //
  // The `L·sin α` term is not a rounding detail. This model applies lift along the *body-up*
  // axis (SPEC §3.2.2), which is tilted α back from the flight-path normal, so the lift vector
  // carries an axial component of its own on top of the separate induced-drag term. Ignoring it
  // yields a "trim" throttle several percent low, and the aircraft quietly decelerates — which
  // reads as a broken flight model rather than a broken trim solver.
  let alphaRad = clamp(
    (weightN / qS - airframe.cl0) / airframe.clAlpha,
    -airframe.alphaStallRad,
    airframe.alphaStallRad,
  );
  let thrustN = 0;

  for (let iteration = 0; iteration < 3; iteration += 1) {
    const sinAlpha = Math.sin(alphaRad);
    const cosAlpha = Math.cos(alphaRad);

    const liftN = (weightN - thrustN * sinAlpha) / cosAlpha;
    alphaRad = clamp(
      (liftN / qS - airframe.cl0) / airframe.clAlpha,
      -airframe.alphaStallRad,
      airframe.alphaStallRad,
    );

    const cl = liftCoefficient(alphaRad, airframe);
    const dragN = qS * dragCoefficient(cl, alphaRad, airframe);
    thrustN = (dragN + qS * cl * Math.sin(alphaRad)) / Math.cos(alphaRad);
  }

  const densityRatio = densityKgM3 / ATMO.RHO_0;
  const throttle = clamp(thrustN / (airframe.thrustMaxN * densityRatio), 0, 1);
  return { alphaRad, throttle };
}

/**
 * Load factor in a coordinated level turn at a given bank angle: n = 1/cos φ.
 *
 * Not used by the integrator — the simulated aircraft reaches this by flying — but it is the
 * reference the autopilot trims against and what the tests check the physics against.
 */
export function levelTurnLoadFactor(bankRad: number): number {
  const cosBank = Math.cos(bankRad);
  return Math.abs(cosBank) < 1e-6 ? Number.POSITIVE_INFINITY : 1 / cosBank;
}
